import { configureStore } from '@reduxjs/toolkit';
import type { ThunkMiddlewareFor } from '@reduxjs/toolkit/src/getDefaultMiddleware';
import type { ExtractDispatchExtensions } from '@reduxjs/toolkit/src/tsHelpers';
import type { Tuple } from '@reduxjs/toolkit/src/utils';
import type {
    Reducer,
    ReducersMapObject,
    StoreEnhancer,
    UnknownAction,
} from 'redux';
import {
    FLUSH,
    PAUSE,
    PERSIST,
    type PersistConfig,
    persistReducer,
    PURGE,
    REGISTER,
    REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { appDataReducer, initAppDataListener } from '@/features/app-data';
import {
    authFailurePopupReducer,
    authFailurePopupListener,
} from '@/features/auth-failure-popup';
import { patternCandlesListener } from '@/features/pattern-lab';
import {
    planMigrationReducer,
    planMigrationListener,
} from '@/features/plan-migration';

import {
    invalidateSessionListener,
    resetSessionListener,
    sessionReducer,
    sessionSlice,
} from '@/entities/auth';
import { notificationReducer } from '@/entities/notification';
import {
    tradingPlatformSessionReducer,
    tradingPlatformSessionSlice,
} from '@/entities/trading-platform';

import { baseApi, baseCMSApi, invalidateSession } from '@/shared/api';

import { ErrorLogger } from '../middlewares/error-logger';
import type { ReducerManager } from '../types/reducer-manager';
import type { ReduxStoreWithReducerManager } from '../types/redux-store-with-reducer-manager';
import type { AppState } from '../types/state';
import type { AppStateSchema } from '../types/state.schema';

import { createReducerManager } from './reducer-manager';

type PreloadedState = Partial<AppState>;
type StoreReducer = Reducer<AppState, UnknownAction, PreloadedState>;
type Middlewares = Tuple<[ThunkMiddlewareFor<AppState>]>;
type Enhancers = Tuple<
    [
        StoreEnhancer<{
            dispatch: ExtractDispatchExtensions<Middlewares>;
        }>,
        StoreEnhancer,
    ]
>;

export function createReduxStore(
    initialState: Partial<AppStateSchema> = {},
    asyncReducers: Partial<ReturnType<ReducerManager['getReducerMap']>> = {},
) {
    const appReducers: ReducersMapObject<AppStateSchema> = {
        session: sessionReducer,
        appData: appDataReducer,
        tradingPlatformSession: tradingPlatformSessionReducer,
        notification: notificationReducer,
        authFailurePopup: authFailurePopupReducer,
        planMigration: planMigrationReducer,
        ...asyncReducers,
        [baseApi.reducerPath]: baseApi.reducer,
        [baseCMSApi.reducerPath]: baseCMSApi.reducer,
    };

    const persistConfig: PersistConfig<AppStateSchema> = {
        key: 'root',
        storage,
        whitelist: [sessionSlice.name, tradingPlatformSessionSlice.name],
    };

    const reducerManager = createReducerManager(appReducers);

    const rootReducer = (
        state: Undefinable<AppStateSchema>,
        action: UnknownAction,
    ) => {
        if (action.type === invalidateSession.type) {
            // eslint-disable-next-line no-param-reassign
            state = undefined;
        }

        return reducerManager.reduce(state, action);
    };

    const store = configureStore<
        AppState,
        UnknownAction,
        Middlewares,
        Enhancers,
        PreloadedState
    >({
        reducer: persistReducer(persistConfig, rootReducer) as StoreReducer,
        preloadedState: initialState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [
                        FLUSH,
                        REHYDRATE,
                        PAUSE,
                        PERSIST,
                        PURGE,
                        REGISTER,
                    ],
                },
            }).concat(
                baseApi.middleware,
                baseCMSApi.middleware,
                ErrorLogger,
                invalidateSessionListener.middleware,
                authFailurePopupListener.middleware,
                initAppDataListener.middleware,
                resetSessionListener.middleware,
                patternCandlesListener.middleware,
                planMigrationListener.middleware,
            ),
    }) as ReduxStoreWithReducerManager;

    store.reducerManager = reducerManager;

    return store;
}

export type AppDispatch = ReturnType<typeof createReduxStore>['dispatch'];
